import { useCallback } from 'react';

import { useRfqBuilderModalMethods } from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/modal-context';
import {
  useRfqBuilderView,
  useSend,
} from '@paradigm/features/src/unified-rfqs/ui';
import { useSubmitted } from '@paradigm/features/src/forms/submitted-context';
import { usePinnedRfqsAction } from '@paradigm/features/src/unified-rfqs/ui/rfqs/pinned-rfqs-context';
import { useDrfqService } from '@paradigm/features/src/unified-rfqs/contexts/drfqs';
import { useHiddenLimitOrderFeatureFlag } from '@paradigm/features/src/feature-flags/context';
import {
  OrderType,
  Side,
} from '@paradigm/features/src/unified-rfqs/entities/shared';
import { TimeInForce } from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';

import { toast } from '#/components/UnifiedRfqs/toast';

import Button from '@paradigm/design-system/src/components/Button';
import { useTrackAction } from '@paradigm/features/src/analytics';

export default function SubmitRfqButton() {
  const rfqBuilderModal = useRfqBuilderModalMethods();
  const {
    isValid,
    auction,
    hedge,
    counterpartiesError,
    hiddenLimitOrder,
    account,
  } = useRfqBuilderView();
  const isObAuctionType = auction.selectedOption.value === 'OB';
  const { isLoading, send } = useSend(isObAuctionType);
  const { setSubmit } = useSubmitted();
  const { pinRfq } = usePinnedRfqsAction();
  const trackAction = useTrackAction();
  const drfqService = useDrfqService();
  const isHiddenLimitOrderEnabled = useHiddenLimitOrderFeatureFlag();

  const createHiddenLimitOrders = useCallback(
    async (rfqId: string, rfqQuantity: string) => {
      if (!hiddenLimitOrder.enabled || !isHiddenLimitOrderEnabled) {
        return;
      }

      if (!account?.selectedOption) {
        toast('Please select an account');
        return;
      }

      const orderArgs = {
        rfq_id: rfqId,
        account_name: account.selectedOption,
        type: 'HIDDEN' as OrderType,
        time_in_force: 'GOOD_TILL_CANCELED' as TimeInForce,
        quantity: rfqQuantity, // RFQ orders must consume full quantity
      };

      const orders = [];

      if (hiddenLimitOrder.bidPrice) {
        orders.push(
          drfqService.createOrReplaceOrder({
            orderId: null,
            order: {
              ...orderArgs,
              side: 'BUY' as Side,
              price: hiddenLimitOrder.bidPrice,
            },
          }),
        );
      }

      if (hiddenLimitOrder.askPrice) {
        orders.push(
          drfqService.createOrReplaceOrder({
            orderId: null,
            order: {
              ...orderArgs,
              side: 'SELL' as Side,
              price: hiddenLimitOrder.askPrice,
            },
          }),
        );
      }

      // drfqService.createOrReplaceOrder does not actually throw
      if (orders.length > 0) {
        try {
          const results = await Promise.all(orders);
          // Check if any of the orders failed
          const failedOrders = results.filter((result) => !result.ok);
          if (failedOrders.length > 0) {
            console.error('Some hidden limit orders failed:', failedOrders);
            toast('Failed to create hidden limit orders');
          }
        } catch (error) {
          console.error('Failed to create hidden limit orders:', error);
          toast('Failed to create hidden limit orders');
        }
      }
    },
    [hiddenLimitOrder, isHiddenLimitOrderEnabled, account, drfqService],
  );

  const handleClick = useCallback(() => {
    trackAction('submit market', {
      auctionType: isObAuctionType ? 'OB' : 'RFQ',
    });

    if (hedge.submitMsg !== undefined) {
      toast(hedge.submitMsg);
      return;
    }

    setSubmit(true);

    if (counterpartiesError.length > 0) {
      toast(counterpartiesError);
      return;
    }

    if (!isValid) return;

    send((resp) => {
      if (resp.ok) {
        const newRfq = resp.data;
        pinRfq(newRfq.id);

        // Create hidden limit orders if enabled (fire and forget)
        if (!isObAuctionType && 'quantity' in newRfq) {
          createHiddenLimitOrders(
            String(newRfq.id),
            String(newRfq.quantity),
          ).catch((error) => {
            console.error('Failed to create hidden limit orders:', error);
            toast('Failed to create hidden limit orders');
          });
        }

        rfqBuilderModal.close();
      } else {
        toast(resp.message);
      }
    });
  }, [
    trackAction,
    isObAuctionType,
    hedge.submitMsg,
    setSubmit,
    counterpartiesError,
    isValid,
    send,
    pinRfq,
    rfqBuilderModal,
    createHiddenLimitOrders,
  ]);

  return (
    <Button
      ariaLabel={auction.selectedOption.submitText}
      size="large"
      type="submit"
      isValid={isValid}
      onClick={handleClick}
      isLoading={isLoading}
    >
      {auction.selectedOption.submitText}
    </Button>
  );
}
